import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Tabs, 
    Tab, 
    Container,
    Paper,
    Stack,
    Chip,
    Avatar,
    useTheme,
    useMediaQuery,
    Grid,
    Card,
    CardContent,
    Skeleton
} from '@mui/material';
import CompanyManagement from '../components/admin/CompanyManagement';
import UserManagement from '../components/admin/UserManagement';
import CustomerAccessManagement from '../components/admin/CustomerAccessManagement';
import DriverSpotlightManagement from '../components/admin/DriverSpotlightManagement';
import AuditManagement from '../components/admin/AuditManagement';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import CompanySettingsCard from '../components/admin/CompanySettingsCard';
import ApiKeysManagement from '../components/admin/ApiKeysManagement';
import IntegrationsManagement from '../components/admin/IntegrationsManagement';
import DataManagement from '../components/admin/DataManagement';
import apiClient from '../api';

// TabPanel component for tab content
function TabPanel(props) {
    const { children, value, index, ...other } = props;
  
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`admin-tabpanel-${index}`}
            aria-labelledby={`admin-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

// Helper function for tab accessibility
function a11yProps(index) {
    return {
        id: `admin-tab-${index}`,
        'aria-controls': `admin-tabpanel-${index}`,
    };
}

function AdminPage() {
    const [tabValue, setTabValue] = useState(0);
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    // Stats state
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalCompanies: 0,
        totalCustomerAccess: 0,
        activeUsers: 0
    });
    const [loading, setLoading] = useState(true);

    // Fetch stats
    useEffect(() => {
        const fetchStats = async () => {
            try {
                console.log('[AdminPage] fetchStats starting...', { isAdmin, user: user?.email, role: user?.role });
                
                if (isAdmin) {
                    console.log('[AdminPage] Fetching admin stats...');
                    
                    // Fetch admin stats with detailed logging
                    console.log('[AdminPage] Making API calls...');
                    const [usersRes, companiesRes] = await Promise.all([
                        apiClient.get('/admin/users').then(res => {
                            console.log('[AdminPage] Users API response:', { 
                                status: res.status, 
                                data: res.data,
                                hasUsers: !!res.data?.data?.users,
                                userCount: res.data?.data?.users?.length || 0
                            });
                            return res;
                        }).catch(err => {
                            console.error('[AdminPage] Users API error:', { 
                                status: err.response?.status, 
                                message: err.message,
                                data: err.response?.data,
                                config: err.config
                            });
                            throw err;
                        }),
                        apiClient.get('/admin/companies').then(res => {
                            console.log('[AdminPage] Companies API response:', { 
                                status: res.status, 
                                data: res.data,
                                hasCompanies: !!res.data?.data?.companies,
                                companyCount: res.data?.data?.companies?.length || 0
                            });
                            return res;
                        }).catch(err => {
                            console.error('[AdminPage] Companies API error:', { 
                                status: err.response?.status, 
                                message: err.message,
                                data: err.response?.data,
                                config: err.config
                            });
                            throw err;
                        })
                    ]);
                    
                    const users = usersRes.data.data?.users || [];
                    const companies = companiesRes.data.data?.companies || [];
                    
                    console.log('[AdminPage] Processing stats data:', { 
                        usersCount: users.length, 
                        companiesCount: companies.length,
                        activeUsers: users.filter(u => u.isActive).length
                    });
                    
                    setStats({
                        totalUsers: users.length,
                        totalCompanies: companies.length,
                        totalCustomerAccess: 0, // Could be calculated if needed
                        activeUsers: users.filter(u => u.isActive).length
                    });
                    
                    console.log('[AdminPage] Stats set successfully');
                } else {
                    console.log('[AdminPage] Fetching company admin stats...');
                    // Fetch company admin stats
                    const usersRes = await apiClient.get('/users/company');
                    const users = usersRes.data.users || [];
                    
                    setStats({
                        totalUsers: users.length,
                        totalCompanies: 1, // Company admin manages one company
                        totalCustomerAccess: 0,
                        activeUsers: users.filter(u => u.isActive).length
                    });
                }
            } catch (error) {
                console.error('[AdminPage] Error fetching stats:', error);
                console.error('[AdminPage] Error details:', {
                    message: error.message,
                    status: error.response?.status,
                    data: error.response?.data,
                    config: error.config
                });
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [isAdmin]);

    // Deep link: select tab based on query param (?tab=company|users|access|spotlight|audit|integrations|api)
    useEffect(() => {
        try {
            const params = new URLSearchParams(location.search || '');
            const tabParam = (params.get('tab') || '').toLowerCase();
            if (!tabParam) return;
            // Map differs for admin vs company_admin due to tab ordering
            const map = isAdmin ? {
                company: 0,
                users: 1,
                access: 2,
                spotlight: 3,
                audit: 4,
                integrations: 5,
                api: 6,
                apikeys: 6,
                datamanagement: 7,
                data: 7
            } : {
                company: 0, // Company Settings
                users: 1,
                access: 2,
                spotlight: 3,
                integrations: 4,
                api: 5,
                apikeys: 5
            };
            const idx = map[tabParam];
            if (typeof idx === 'number') setTabValue(idx);
        } catch (_) {}
    }, [location.search, isAdmin]);

    const handleTabChange = (event, newValue) => {
        console.log('[AdminPage] Tab change', { newValue, isAdmin });
        setTabValue(newValue);
    };

    useEffect(() => {
        console.log('[AdminPage] Active tab value', { tabValue, isAdmin });
    }, [tabValue, isAdmin]);

    const statNumber = (value) => (loading ? (
        <Skeleton variant="text" width={64} height={isMobile ? 40 : 56} sx={{ bgcolor: 'rgba(255,255,255,0.35)' }} />
    ) : (
        <Typography variant={isMobile ? 'h4' : 'h3'} sx={{ fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
    ));

    const renderStatsCards = () => (
        <Grid container spacing={3} sx={{ mb: 4 }}>
            {isAdmin && (
                <Grid item xs={12} sm={6} md={3}>
                    <Card 
                        sx={{ 
                            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                            color: 'white',
                            height: '100%'
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography variant="overline" sx={{ fontWeight: 600, opacity: 0.9 }}>
                                        Total Companies
                                    </Typography>
                                    {statNumber(stats.totalCompanies)}
                                </Box>
                                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: { xs: 48, md: 56 }, height: { xs: 48, md: 56 } }}>
                                    <BusinessIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
                                </Avatar>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            )}

            <Grid item xs={12} sm={6} md={isAdmin ? 3 : 4}>
                <Card 
                    sx={{ 
                        background: 'linear-gradient(135deg, #388e3c 0%, #66bb6a 100%)',
                        color: 'white',
                        height: '100%'
                    }}
                >
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="overline" sx={{ fontWeight: 600, opacity: 0.9 }}>
                                    Total Users
                                </Typography>
                                {statNumber(stats.totalUsers)}
                            </Box>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: { xs: 48, md: 56 }, height: { xs: 48, md: 56 } }}>
                                <PeopleIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
                            </Avatar>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={isAdmin ? 3 : 4}>
                <Card 
                    sx={{ 
                        background: 'linear-gradient(135deg, #f57c00 0%, #ffb74d 100%)',
                        color: 'white',
                        height: '100%'
                    }}
                >
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="overline" sx={{ fontWeight: 600, opacity: 0.9 }}>
                                    Active Users
                                </Typography>
                                {statNumber(stats.activeUsers)}
                            </Box>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: { xs: 48, md: 56 }, height: { xs: 48, md: 56 } }}>
                                <AssignmentIndIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
                            </Avatar>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={isAdmin ? 3 : 4}>
                <Card 
                    sx={{ 
                        background: 'linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%)',
                        color: 'white',
                        height: '100%'
                    }}
                >
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="overline" sx={{ fontWeight: 600, opacity: 0.9 }}>
                                    Access Control
                                </Typography>
                                {statNumber(`${Math.round((stats.activeUsers / Math.max(stats.totalUsers, 1)) * 100)}%`)}
                                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                                    User activation rate
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: { xs: 48, md: 56 }, height: { xs: 48, md: 56 } }}>
                                <SecurityIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
                            </Avatar>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );

    return (
        <Container maxWidth="xl" disableGutters sx={{ pt: 0, pb: { xs: 0.5, md: 1 } }}>
            {/* Stats Cards */}
            {renderStatsCards()}

            {/* Company Settings moved to its own tab for company_admins */}

            {/* Main Content */}
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', borderTop: '3px solid', borderTopColor: 'primary.main' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                    <Tabs 
                        value={tabValue} 
                        onChange={handleTabChange} 
                        aria-label="admin management tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{ 
                            px: 2,
                            '& .MuiTab-root': {
                                minHeight: { xs: 60, md: 72 },
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: { xs: '0.875rem', md: '1rem' }
                            }
                        }}
                    >
                        {/* Only show Companies tab to site admin */}
                        {isAdmin && (
                            <Tab 
                                icon={<BusinessIcon />}
                                label={isMobile ? "Companies" : "Company Management"} 
                                iconPosition="start"
                                {...a11yProps(0)} 
                            />
                        )}
                        {!isAdmin && (
                            <Tab 
                                icon={<SettingsIcon />}
                                label={isMobile ? "Settings" : "Company Settings"} 
                                iconPosition="start"
                                {...a11yProps(0)} 
                            />
                        )}
                        
                        <Tab 
                            icon={<PeopleIcon />}
                            label={isMobile ? "Users" : "User Management"}
                            iconPosition="start"
                            {...a11yProps(1)} 
                        />
                        
                        <Tab 
                            icon={<SecurityIcon />}
                            label={isMobile ? "Access" : "Customer Access"} 
                            iconPosition="start"
                            {...a11yProps(2)} 
                        />

                        <Tab 
                            icon={<EmojiEventsIcon />}
                            label={isMobile ? "Driver Spotlight" : "Driver Spotlight"} 
                            iconPosition="start"
                            {...a11yProps(3)} 
                        />

                        {isAdmin && (
                            <Tab 
                                icon={<AssessmentIcon />}
                                label={isMobile ? "Audit" : "System Audit"} 
                                iconPosition="start"
                                {...a11yProps(4)} 
                            />
                        )}
                        {(isAdmin || user?.role === 'company_admin') && (
                        <Tab 
                            icon={<SettingsIcon />}
                            label={isMobile ? "Integrations" : "Integrations"} 
                            iconPosition="start"
                                {...a11yProps(isAdmin ? 5 : 4)} 
                        />
                    )}
                        {(isAdmin || user?.role === 'company_admin') && (
                        <Tab 
                            icon={<SettingsIcon />}
                            label={isMobile ? "API Keys" : "API Keys"} 
                            iconPosition="start"
                                {...a11yProps(isAdmin ? 6 : 5)} 
                        />
                    )}
                    {isAdmin && (
                        <Tab 
                            icon={<SettingsIcon />}
                            label={isMobile ? "Data Mgmt" : "Data Management"} 
                            iconPosition="start"
                            {...a11yProps(7)} 
                        />
                    )}
                    </Tabs>
                </Box>
                
                <Box sx={{ bgcolor: 'background.paper' }}>
                    {isAdmin && (
                        <TabPanel value={tabValue} index={0} data-usage-screen="admin.companies">
                            <CompanyManagement />
                        </TabPanel>
                    )}
                    
                    {!isAdmin && (
                        <TabPanel value={tabValue} index={0} data-usage-screen="admin.settings">
                            <CompanySettingsCard />
                        </TabPanel>
                    )}

                    <TabPanel value={tabValue} index={1} data-usage-screen="admin.users">
                        <UserManagement />
                    </TabPanel>
                    
                    <TabPanel value={tabValue} index={2} data-usage-screen="admin.access">
                        <CustomerAccessManagement />
                    </TabPanel>

                    <TabPanel value={tabValue} index={3} data-usage-screen="admin.spotlight">
                        <DriverSpotlightManagement />
                    </TabPanel>

                    {isAdmin && (
                        <TabPanel value={tabValue} index={4} data-usage-screen="admin.audit">
                            <AuditManagement />
                        </TabPanel>
                    )}

                    {(isAdmin || user?.role === 'company_admin') && (
                        <TabPanel value={tabValue} index={isAdmin ? 5 : 4} data-usage-screen="admin.integrations">
                            <IntegrationsManagement />
                        </TabPanel>
                    )}
                    {(isAdmin || user?.role === 'company_admin') && (
                        <TabPanel value={tabValue} index={isAdmin ? 6 : 5} data-usage-screen="admin.api-keys">
                            {console.log('[AdminPage] Rendering API Keys panel')}
                            <ApiKeysManagement />
                        </TabPanel>
                    )}
                    {isAdmin && (
                        <TabPanel value={tabValue} index={7} data-usage-screen="admin.data">
                            <DataManagement />
                        </TabPanel>
                    )}
                </Box>
            </Paper>
        </Container>
    );
}

export default AdminPage; 